"use client";
import { useEffect, useRef, useCallback } from "react";
import Loading from "../../common/components/Loading";

interface InfiniteScrollLoaderProps {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => Promise<void> | void;
  className?: string;
  threshold?: number;
  rootMargin?: string;
}

const InfiniteScrollLoader = ({
  hasMore,
  loading,
  onLoadMore,
  className = "",
  threshold = 0.1,
  rootMargin = "100px",
}: InfiniteScrollLoaderProps) => {
  const loaderRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading) {
        onLoadMore();
      }
    },
    [hasMore, loading, onLoadMore],
  );

  useEffect(() => {
    const loader = loaderRef.current;
    if (!loader) return;

    // Disconnect existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin,
    });

    // Start observing
    observerRef.current.observe(loader);

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, threshold, rootMargin]);

  // Don't render anything if there's no more content to load
  if (!hasMore && !loading) {
    return null;
  }

  return (
    <div
      ref={loaderRef}
      className={`infinite-scroll-loader ${className}`}
      style={{ minHeight: "20px" }}
    >
      {loading && <Loading className="border-top" />}
    </div>
  );
};

export default InfiniteScrollLoader;
